import type { PointsBreakdownEvent } from '@brush/api-client';
import { participationEntriesLabel } from '@brush/ui';
import type { ReactNode } from 'react';
import { usePlayer } from '../player';

/**
 * One event's contribution, on the player's side of the glass — the portal's rendering of
 * `PointsBreakdownEventDto`.
 *
 * <b>Extracted because ratings §7 nests this very object inside a rating's breakdown and requires it
 * rendered VERBATIM, by the existing component rather than by a second one.</b> The fantasy F6 defect
 * was a label reading the wrong field, and the fix rule was one pure function both call sites use;
 * this is that rule applied to a component. `LeaderboardBreakdown` and `RatingBreakdown` render the
 * same card, and neither reshapes what is inside it.
 *
 * <b>Still player-web's own and deliberately not `packages/ui`'s.</b> That package already renders
 * this payload for an operator testing a points system — dense, tabular, a third of a screen wide.
 * This is read once, by the person it is about, on a phone. Two layouts, one shape, one set of
 * server-computed figures, and the note in "Next up" about moving one of them was about a second
 * *console* wanting the operator's view.
 */

/**
 * The badge is the visual hook for the whole feature: where the deal rule moved a player, it says so
 * as `1st → 3rd` before anybody reads a word. Where it did not, there is nothing to reconcile and no
 * badge — a screen that marked every ordinary finish "1st → 1st" would spend the reader's attention
 * on the case that needs none.
 */
/**
 * One event's contribution.
 *
 * The badge is the visual hook for the whole feature: where the deal rule moved a player, it says
 * so as `1st → 3rd` before anybody reads a word. Where it did not, there is nothing to reconcile
 * and no badge — a screen that marked every ordinary finish "1st → 1st" would spend the reader's
 * attention on the case that needs none.
 */
export function BreakdownEventCard({
  event,
  places,
  dimmed = false,
  children,
}: {
  event: PointsBreakdownEvent;
  places: number;

  /**
   * Dim this card for a reason of the caller's own.
   *
   * A rating's reason is not this component's: `counted` is always true on a rating's rows, so the
   * dimming below never fires there — what dims a rating's row is §3.3's `selected`, a fact about the
   * rating layer. OR-ed rather than replacing, so §3.9's own dimming is untouched.
   */
  dimmed?: boolean;

  /**
   * The caller's own material, rendered inside this card under the engine's sentence — a rating's
   * decayed figure and §3.3's selection note. A slot rather than a wrapper because this returns an
   * `li`, and nesting one list item inside another is not HTML.
   */
  children?: ReactNode;
}) {
  const { theme } = usePlayer();

  const moved =
    event.place !== null && event.effectivePlace !== null && event.place !== event.effectivePlace;

  return (
    <li
      className="rounded-lg p-4"
      style={{
        background: theme.panelLight,
        border: `1px solid ${theme.border}`,
        // §3.9's bestN drops events from the total but not from the audit trail. Dimmed rather
        // than hidden, because "why didn't my ninth-best result count" is a question this screen
        // should answer, and an event that is simply absent answers nothing.
        opacity: event.counted && !dimmed ? 1 : 0.6,
      }}
    >
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">{event.eventName}</div>
          <div className="mt-0.5 text-xs" style={{ color: theme.textMuted }}>
            {event.eventDate}
            {event.place === null ? ' · did not cash' : ` · finished ${ordinal(event.place)}`}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="font-bold" style={{ color: theme.accent }}>
            {formatPoints(event.eventScore, places)}
          </div>
          <div className="text-[10px] uppercase tracking-wider" style={{ color: theme.textMuted }}>
            {event.counted ? 'pts' : 'not counted'}
          </div>
        </div>
      </div>

      {moved ? (
        <div className="mt-2">
          <span
            className="inline-block rounded px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider"
            style={{ background: theme.border, color: theme.text }}
          >
            {ordinal(event.place!)} → scored as {ordinal(event.effectivePlace!)}
          </span>
        </div>
      ) : null}

      {/* The server's sentence, verbatim and at reading size. §7.4: not decoration. */}
      <p className="mt-2 text-sm leading-relaxed">{event.explanation}</p>

      <Figures event={event} places={places} />

      {event.warnings.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {event.warnings.map((warning) => (
            <li key={warning} className="text-xs" style={{ color: theme.textMuted }}>
              Note: {warning}
            </li>
          ))}
        </ul>
      ) : null}

      {children}
    </li>
  );
}

/**
 * The arithmetic, corroborating the sentence above it.
 *
 * **A multiplier of exactly 1 is omitted**, and that is the same rule the engine applies when it
 * writes the explanation: a system with no buy-in weighting reads "50 × 1.8 = 90.00", never
 * "50 × 1.8 × 1.0 = 90.00", because the second invites the question of where the 1.0 came from and
 * the answer is nowhere. The payload cannot distinguish "not configured" from "configured and
 * neutral" — both arrive as 1.0 — but for a reader they are the same thing: something that changed
 * nothing. Dropping both keeps this panel and the sentence saying the same words.
 */
function Figures({ event, places }: { event: PointsBreakdownEvent; places: number }) {
  const { theme } = usePlayer();

  const figures: [string, string][] = [];

  if (event.place !== null) figures.push(['Base', formatNumber(event.baseAward)]);
  if (event.fieldMultiplier !== 1) figures.push(['Field size', `× ${formatNumber(event.fieldMultiplier)}`]);
  if (event.buyInMultiplier !== 1) figures.push(['Buy-in', `× ${formatNumber(event.buyInMultiplier)}`]);
  // The label is the designer panel's own (`participationEntriesLabel`), so counted once per
  // tournament it names the entries the sentence above names — "4 entries, counted once" — rather
  // than the one entry that was counted.
  if (event.participationEntries > 0) {
    figures.push([participationEntriesLabel(event), formatPoints(event.participationPoints, places)]);
  }

  if (figures.length === 0) return null;

  return (
    <dl className="mt-3 flex flex-wrap gap-x-5 gap-y-1">
      {figures.map(([label, value]) => (
        <div key={label} className="flex items-baseline gap-1.5">
          <dt className="text-[11px] uppercase tracking-wider" style={{ color: theme.textMuted }}>
            {label}
          </dt>
          <dd className="text-xs font-semibold tabular-nums">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Rounded away from zero, matching the engine — .NET's `MidpointRounding.AwayFromZero`, and not
 * JavaScript's default. `toFixed` alone rounds a binary approximation, so a figure the server
 * renders as 90.13 inside its explanation can come out 90.12 in the column beside it, which is the
 * one discrepancy on this screen a reader would take as an error rather than as rounding.
 */
export function formatPoints(value: number, places: number): string {
  const factor = 10 ** places;
  const rounded = (Math.sign(value) * Math.round(Math.abs(value) * factor)) / factor;
  return rounded.toFixed(places);
}

/** A multiplier or an award, without trailing zeros: 1.8 rather than 1.8000. The engine's `0.####`. */
function formatNumber(value: number): string {
  return String(Math.round(value * 10000) / 10000);
}

export function ordinal(n: number): string {
  const suffix =
    n % 100 >= 11 && n % 100 <= 13
      ? 'th'
      : { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th';

  return `${n}${suffix}`;
}

/** An ISO timestamp as something a player reads. Falls back to the raw value rather than NaN. */
export function formatWhen(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}
